import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Linkedin, Sparkles, Loader2, Phone, Briefcase } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Honeypot from "@/components/Honeypot";
import { z } from "zod";

const schema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(100, "Name is too long"),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  company: z.string().trim().max(100).optional().or(z.literal("")),
  businessType: z.string().max(100).optional().or(z.literal("")),
  message: z.string().trim().min(10, "Please tell us a bit more (10+ characters)").max(1000, "Message is too long"),
});

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", businessType: "", message: "",
  });
  const [hp, setHp] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiResponse, setAiResponse] = useState<string | null>(null);

  const validateField = (name: string, value: string) => {
    const fieldSchema = (schema.shape as Record<string, z.ZodTypeAny>)[name];
    if (!fieldSchema) return;
    const r = fieldSchema.safeParse(value);
    setErrors((e) => ({ ...e, [name]: r.success ? "" : r.error.issues[0]?.message ?? "" }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) validateField(name, value);
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    validateField(e.target.name, e.target.value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Honeypot, silently succeed
    if (hp.trim()) {
      setForm({ name: "", email: "", phone: "", company: "", businessType: "", message: "" });
      setConsent(false);
      toast({ title: "Message sent!", description: "We'll be in touch shortly." });
      return;
    }
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      parsed.error.issues.forEach((i) => { fieldErrors[i.path[0] as string] = i.message; });
      setErrors(fieldErrors);
      toast({ title: "Please check the form", description: "A few fields need attention.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    setAiResponse(null);
    try {
      const { data, error } = await supabase.functions.invoke("contact-ai", {
        body: { ...parsed.data, _hp: hp },
      });
      if (error) throw error;
      setAiResponse(data?.response ?? null);
      setForm({ name: "", email: "", phone: "", company: "", businessType: "", message: "" });
      setErrors({});
      setConsent(false);
      toast({ title: "Message sent!", description: "We've received your message and will follow up soon." });
    } catch (err) {
      console.error("Submit error:", err);
      toast({
        title: "We couldn't send your message",
        description: "Please try again in a moment, or email abdelrahaman@elevateglobalsolutions.net.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactSchema = {
    "@context": "https://schema.org", "@type": "ContactPage",
    name: "Contact Elevate Global Solutions",
    url: "https://www.elevateglobalsolutions.net/contact",
    mainEntity: { "@id": "https://www.elevateglobalsolutions.net/#organization" },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.elevateglobalsolutions.net/" },
      { "@type": "ListItem", position: 2, name: "Contact", item: "https://www.elevateglobalsolutions.net/contact" },
    ],
  };

  const OG_IMG = "https://www.elevateglobalsolutions.net/og-default.jpg";

  return (
    <>
      <Helmet>
        <title>Contact Us | Elevate Global Solutions</title>
        <meta name="description" content="Hire Egyptian cold callers, closers, and virtual assistants, or outsource lead generation and appointment setting. We respond within 24 hours." />
        <meta property="og:title" content="Contact Us , Hire Egyptian Cold Callers & Closers" />
        <meta property="og:description" content="Get in touch to hire Egyptian cold callers, closers, and virtual assistants for lead generation and appointment setting." />
        <meta property="og:url" content="https://www.elevateglobalsolutions.net/contact" />
        <meta property="og:image" content={OG_IMG} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG_IMG} />
        <meta name="twitter:title" content="Contact Us | Elevate Global Solutions" />
        <meta name="twitter:description" content="Get in touch to hire Egyptian cold callers, closers, and virtual assistants for lead generation and appointment setting." />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/contact" />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

    <div>
      <section className="bg-primary section-padding py-16 md:py-24">
        <div className="container-narrow text-center">
          <h1 className="text-4xl font-extrabold text-primary-foreground md:text-5xl">Contact Us</h1>
          <p className="mt-4 text-lg text-primary-foreground/80">We'd love to hear from you. Let's start a conversation.</p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-narrow">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Send Us a Message</h2>
              <p className="mt-2 text-muted-foreground">Fill out the form below and we'll respond within 24 hours.</p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-5 relative" noValidate>
                <Honeypot value={hp} onChange={setHp} />

                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" placeholder="Your full name" value={form.name}
                    onChange={handleChange} onBlur={handleBlur} required maxLength={100}
                    aria-invalid={!!errors.name} disabled={isSubmitting} />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" placeholder="you@company.com" value={form.email}
                    onChange={handleChange} onBlur={handleBlur} required maxLength={255}
                    aria-invalid={!!errors.email} disabled={isSubmitting} />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input id="phone" name="phone" type="tel" placeholder="(555) 123-4567" value={form.phone}
                    onChange={handleChange} onBlur={handleBlur} maxLength={30} disabled={isSubmitting} />
                  {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input id="company" name="company" placeholder="Your company name" value={form.company}
                    onChange={handleChange} onBlur={handleBlur} maxLength={100} disabled={isSubmitting} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessType">What type of business do you run?</Label>
                  <Select value={form.businessType}
                    onValueChange={(value) => setForm((p) => ({ ...p, businessType: value }))}
                    disabled={isSubmitting}>
                    <SelectTrigger><SelectValue placeholder="Select your industry" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Real Estate">Real Estate</SelectItem>
                      <SelectItem value="Insurance">Insurance</SelectItem>
                      <SelectItem value="Solar / Energy">Solar / Energy</SelectItem>
                      <SelectItem value="Home Services">Home Services</SelectItem>
                      <SelectItem value="Healthcare">Healthcare</SelectItem>
                      <SelectItem value="E-commerce">E-commerce</SelectItem>
                      <SelectItem value="SaaS / Tech">SaaS / Tech</SelectItem>
                      <SelectItem value="Financial Services">Financial Services</SelectItem>
                      <SelectItem value="Marketing Agency">Marketing Agency</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Tell us briefly what you need</Label>
                  <Textarea id="message" name="message"
                    placeholder="e.g., I'm looking for help with lead generation for my roofing business..."
                    value={form.message} onChange={handleChange} onBlur={handleBlur}
                    required maxLength={1000} rows={5}
                    aria-invalid={!!errors.message} disabled={isSubmitting} />
                  {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox id="consent" checked={consent}
                    onCheckedChange={(c) => setConsent(c === true)}
                    disabled={isSubmitting} className="mt-0.5" />
                  <Label htmlFor="consent" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">
                    I agree to share my information with Elevate Global Solutions so we can respond to my inquiry. We use industry-standard security practices to protect your information. We never sell your personal data and do not share it with third parties for advertising or marketing purposes. Information submitted through our website is used only to provide services, respond to inquiries, improve website performance, and support legitimate business operations.
                  </Label>
                </div>

                <Button type="submit" size="lg"
                  className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
                  disabled={isSubmitting || !consent}>
                  {isSubmitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                  ) : "Send Message"}
                </Button>
              </form>

              {aiResponse && (
                <div className="mt-6 rounded-xl border border-secondary/30 bg-secondary/5 p-5">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-secondary">
                    <Sparkles size={16} />Personalized Response
                  </div>
                  <p className="text-sm leading-relaxed text-foreground">{aiResponse}</p>
                </div>
              )}
            </div>

            <div>
              <h2 className="text-2xl font-bold text-foreground">Get in Touch</h2>
              <p className="mt-2 text-muted-foreground">Prefer to reach out directly? Here's how.</p>
              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-primary"><Mail size={20} /></div>
                  <div>
                    <h3 className="font-semibold text-foreground">Email</h3>
                    <a href="mailto:abdelrahaman@elevateglobalsolutions.net" className="text-sm text-primary hover:underline">abdelrahaman@elevateglobalsolutions.net</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-primary"><Phone size={20} /></div>
                  <div>
                    <h3 className="font-semibold text-foreground">Phone</h3>
                    <a href="tel:+12142962124" className="text-sm text-primary hover:underline">(214) 296-2124</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-primary"><MapPin size={20} /></div>
                  <div>
                    <h3 className="font-semibold text-foreground">Location</h3>
                    <p className="text-sm text-muted-foreground">Cairo, Egypt , Serving clients across the United States</p>
                  </div>
                </div>
              </div>
              <div className="mt-10">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">Follow Us</h3>
                <div className="mt-3 flex gap-3">
                  {[{ icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/elevate-global-solutions-egypt/" }].map(({ icon: Icon, label, href }) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;
